import React from 'react'
import { FormattedMessage } from 'react-intl'
import Button from '@vtex/styleguide/lib/Button'
import Tag from '@vtex/styleguide/lib/Tag'

import Card from '@/checkout-instore-legacy/components/Commons/Card'
import { DeliveryPackage } from '@/checkout-instore-legacy/components/Commons/Icons/DeliveryPackage'
import { PickupPackage } from '@/checkout-instore-legacy/components/Commons/Icons/PickupPackage'
import { Inventory } from '@/checkout-instore-legacy/components/Commons/Icons/Inventory'
import ProductSummary from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductSummary'
import ProductName from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductName'
import ProductImage from '@/checkout-instore-legacy/components/Commons/ProductSummary/ProductImage'
import {
  getDefaultSellerId,
  getOriginSellerFromItem,
} from '@/checkout-instore-legacy/seller/utils'
import { ChangeSelector } from '@/checkout-instore-legacy/shipping/components/SlaSelector/ChangeSelector'
import {
  calculatePackageUnavailability,
  getShippingEstimateMessage,
  maskPostalCode,
} from '@/checkout-instore-legacy/shipping/utils'
import { useMediaQuery } from '@/checkout-instore-legacy/hooks/mediaQuery'
import { PICKUP_DELIVERY_CHANNEL } from '@/checkout-instore-legacy/shipping/constants'
import { Flags } from './ChooseSelectorType'
import { PackageInfo, PackageInfoRow, PackageInfoSkeleton } from './PackageInfo'
import Price from '@/checkout-instore-legacy/components/Commons/Price'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'

const DELIVERY = 'delivery'
const PICKUP = 'pickup-in-point'
const TAKE_AWAY = 'take-away'

type PossibleSlaStates = typeof DELIVERY | typeof PICKUP | typeof TAKE_AWAY

export type Package = {
  pkgHeaderIndex: number
  items: Item[]
  shippingEstimate: string
  price: number
  selectedSlaType: PossibleSlaStates
  deliveryChannel: PossibleSlaStates
  availableDeliveryWindows: any[]
  selectedSlaObj: SLA
  isValid: boolean
  pkgTotalCount: number
  deliveryWindow: any
  selectedSla: SLA
}

type PackageCardProps = {
  type: PossibleSlaStates
  slaPackage: Package
  logisticsInfo: LogisticsInfo
  onSlaSelection: (index: number, sla: SLA) => void
  flags: Partial<Flags>
  postalCode: string
  onSchedulePackage: (slaPackage: Package) => void
  onSelectPickupPoint: (slaPackage: Package) => void
  intl: IntlObject
  index: number
  sellers?: Seller[]
}

const getIconByType = (type: PackageCardProps['type']) => {
  switch (type) {
    case 'delivery':
      return DeliveryPackage
    case 'pickup-in-point':
      return PickupPackage
    case 'take-away':
      return Inventory
  }
}

const SIZES_MOBILE = {
  wrapper: 32,
  icon: 18,
}

const SIZES_DESKTOP = {
  wrapper: 48,
  icon: 32,
}

const PRODUCT_IMAGE_SIZE = {
  tablet: 80,
  phone: 72,
}

const PackageCard = function ({
  type,
  slaPackage,
  logisticsInfo,
  flags,
  onSlaSelection,
  postalCode,
  onSchedulePackage,
  onSelectPickupPoint,
  intl,
  index,
  sellers,
}: PackageCardProps) {
  const Icon = getIconByType(type)
  const {
    pkgHeaderIndex,
    items,
    shippingEstimate,
    price,
    selectedSlaType,
    selectedSlaObj,
    isValid,
  } = slaPackage

  const { isCustomSelected, isScheduledDeliverySelected } = flags

  const { value: size } = useMediaQuery<typeof SIZES_MOBILE>({
    phone: SIZES_MOBILE,
    tablet: SIZES_DESKTOP,
    desktop: SIZES_DESKTOP,
  })

  const { showItemOriginSeller } = getInstoreConfig()

  const checkIsSchedulable = (selectedSlaObj: SLA) => {
    return (
      selectedSlaObj &&
      selectedSlaObj.availableDeliveryWindows &&
      selectedSlaObj.availableDeliveryWindows.length > 0
    )
  }

  const checkIsPickup = (selectedSlaObj: SLA) => {
    return (
      selectedSlaObj &&
      selectedSlaObj.deliveryChannel === PICKUP_DELIVERY_CHANNEL
    )
  }

  const calculateButtonArgs = () => {
    const isSchedulable = checkIsSchedulable(selectedSlaObj)
    const isScheduled = selectedSlaObj && !!selectedSlaObj.deliveryWindow
    const isPickup = checkIsPickup(selectedSlaObj)

    if (isSchedulable) {
      if (!isValid) {
        return {
          handleClick: () => {
            onSchedulePackage(slaPackage)
          },
          label: (
            <FormattedMessage id="shippingConfirmScheduledDeliveryLabel" />
          ),
          disabled: type === TAKE_AWAY,
        }
      } else if (isScheduled) {
        return {
          handleClick: () => {
            onSchedulePackage(slaPackage)
          },
          label: <FormattedMessage id="shippingChangeScheduling" />,
          disabled: type === TAKE_AWAY,
        }
      }
    }

    if (isPickup) {
      return {
        handleClick: () => {
          onSelectPickupPoint(slaPackage)
        },
        label: <FormattedMessage id="shippingChoosePickupPoint" />,
        disabled: type === TAKE_AWAY,
      }
    }
    return null
  }

  const renderButton = () => {
    const buttonArgs = calculateButtonArgs()
    if (buttonArgs && type !== TAKE_AWAY) {
      return (
        <Button
          variation="secondary"
          size="small"
          onClick={buttonArgs.handleClick}
          disabled={buttonArgs.disabled}
          testId={`package-${type}-button`}
          block
        >
          {buttonArgs.label}
        </Button>
      )
    }
    return null
  }

  const getPickupAddress = (selectedSlaObj: SLA) => {
    const friendlyName = selectedSlaObj.pickupStoreInfo?.friendlyName
    const postalCode = selectedSlaObj.pickupStoreInfo?.address?.postalCode

    return `${friendlyName} - ${intl.formatMessage({
      id: 'shippingPostalCode',
    })} ${maskPostalCode(postalCode)}`
  }

  const renderPackageInfo = () => {
    const mainSellerId = items[0].seller || getDefaultSellerId()
    const originSellerId = getOriginSellerFromItem(items[0])
    const originSeller = sellers?.find((seller) => seller.id === originSellerId)

    switch (type) {
      case 'delivery':
        return (
          <PackageInfo sellerId={mainSellerId}>
            <PackageInfoRow
              labelId="shippingTimeEstimate"
              value={getShippingEstimateMessage(shippingEstimate)}
            />
            <PackageInfoRow
              labelId="shippingPrice"
              // TODO: this error popped up when moving the checkout-instore project
              // to checkout-instore-legacy, but needs to be fixed at some point
              // @ts-ignore
              value={<Price value={price} />}
            />
            <PackageInfoRow
              labelId="shippingPostalCode"
              value={maskPostalCode(postalCode)}
            />
            {showItemOriginSeller && (
              <PackageInfoRow
                labelId="shippingOriginSeller"
                value={
                  originSeller ? originSeller.name : <PackageInfoSkeleton />
                }
              />
            )}
          </PackageInfo>
        )
      case 'pickup-in-point':
        return (
          <PackageInfo sellerId={mainSellerId}>
            <PackageInfoRow
              labelId="shippingTimeEstimate"
              value={getShippingEstimateMessage(shippingEstimate)}
            />
            <PackageInfoRow
              labelId="shippingTakeAwayPlace"
              value={getPickupAddress(selectedSlaObj)}
            />
            {showItemOriginSeller && (
              <PackageInfoRow
                labelId="shippingOriginSeller"
                value={
                  originSeller ? originSeller.name : <PackageInfoSkeleton />
                }
              />
            )}
          </PackageInfo>
        )

      case 'take-away':
        return null
    }
  }

  const unavailability = calculatePackageUnavailability(slaPackage, flags)
  const isDefaultSeller = items[0].seller === getDefaultSellerId()

  const getUnavailabilityTag = (unavailabilityPackage: string) => {
    switch (unavailabilityPackage) {
      case 'no-schedulable':
        return <FormattedMessage id="shippingNonSchedulable" />
      case 'pickup-unavailable':
        return <FormattedMessage id="shippingPickupUnavailable" />
      case 'delivery-unavailable':
        return <FormattedMessage id="shippingDeliveryUnavailable" />
      default:
        return null
    }
  }
  const canChangeSla = isCustomSelected || isScheduledDeliverySelected

  return (
    <Card
      id={`package-card-${type}-${index}`}
      data-testid={`package-card-${type}-${index}`}
      className=" flex flex-row pa5"
      borderType="basic"
    >
      <div className="flex mr4  justify-center items-start ">
        <div className="flex  justify-center items-center">
          <div
            className=" ba bw1-ns br-100 flex justify-center b--near-black items-center"
            style={{
              minWidth: size.wrapper,
              minHeight: size.wrapper,
            }}
          >
            <span>
              <Icon width={size.icon} height={size.icon} color="#3f3f40" />
            </span>
          </div>
        </div>
      </div>
      <div className="flex w-100 ">
        <div className="w-100">
          <div
            className={`flex justify-between c-on-base t-heading-5 fw7 mt2 ${
              isDefaultSeller ? 'mb4' : ''
            }`}
          >
            <span data-testid="package-card-title">
              <FormattedMessage
                id="packageTitle"
                values={{
                  index: pkgHeaderIndex,
                  numberOfPackages: slaPackage.pkgTotalCount,
                  type: selectedSlaType.split('-')[0],
                }}
              />
            </span>
            {unavailability ? (
              <span className="ml3">
                <Tag type="warning" size="small">
                  {getUnavailabilityTag(unavailability)}
                </Tag>
              </span>
            ) : null}
          </div>
          <div
            data-testid="package-header"
            className="flex flex-row-ns flex-column-s justify-between-ns items-end-ns mb5"
          >
            {renderPackageInfo()}
            <div className="mt0-ns mt4-s">{renderButton()}</div>
          </div>
          <div data-testid="package-products" className="w-100">
            {items.map((item) => (
              <div key={item.id} className="mv3">
                <ProductSummary
                  className="pa3"
                  item={item}
                  borderType="default"
                >
                  <ProductSummary.Image>
                    <ProductImage
                      src={item.imageUrl}
                      alt={item.name}
                      sizes={PRODUCT_IMAGE_SIZE}
                    />
                  </ProductSummary.Image>
                  <ProductSummary.Name
                    className={`w3-s ${canChangeSla ? 'mt4' : 'mt6 pt2 '}`}
                  >
                    <ProductName showInfo={false} />
                  </ProductSummary.Name>
                  <ProductSummary.Content>
                    {canChangeSla ? (
                      <div className="mb5">
                        <ChangeSelector
                          // @ts-expect-error ts-migrate(2538) FIXME: Type 'null' cannot be used as an index type.
                          logisticsInfoItem={logisticsInfo[item.index]}
                          // @ts-expect-error ts-migrate(2322) FIXME: Type 'Optional<number>' is not assignable to type ... Remove this comment to see the full error message
                          index={item.index}
                          onSlaSelection={onSlaSelection}
                        />
                      </div>
                    ) : null}
                  </ProductSummary.Content>
                </ProductSummary>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  )
}

export { PackageCard }
